import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock,
  ClipboardCheck,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  Clock,
  BarChart3,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { ReactNode } from 'react';
import type { OrderStatus, SubscriptionStatus, PaceCode } from '@ellie/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PipelineOrder {
  id: string;
  status: OrderStatus;
  user_id: string | null;
  subscription_id: string | null;
  updated_at: string;
  created_at: string;
  profiles: { full_name: string | null; email: string } | null;
}

interface RenewalSubscription {
  id: string;
  user_id: string;
  status: SubscriptionStatus;
  pace: PaceCode;
  current_period_end: string | null;
  profiles: { full_name: string | null; email: string } | null;
}

type RangeFilter = 7 | 14 | 30;

type AnalyticsRange = 30 | 90;

/** A completed order with the timestamps needed for stage duration analytics. */
interface CompletedOrder {
  paid_at: string | null;
  generated_at: string | null;
  approved_at: string | null;
  delivered_at: string | null;
}

interface StageDurationStats {
  label: string;
  /** Color for the bar chart segment. */
  color: string;
  values: number[];
  min: number;
  median: number;
  avg: number;
  max: number;
}

// Pipeline stages in order
const PIPELINE_STAGES: OrderStatus[] = ['paid', 'generating', 'review', 'approved', 'delivering'];

const STAGE_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  paid: { bg: 'bg-blue-50', text: 'text-blue-700', bar: 'bg-blue-500' },
  generating: { bg: 'bg-purple-50', text: 'text-purple-700', bar: 'bg-purple-500' },
  review: { bg: 'bg-yellow-50', text: 'text-yellow-700', bar: 'bg-yellow-500' },
  approved: { bg: 'bg-green-50', text: 'text-green-700', bar: 'bg-green-500' },
  delivering: { bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' },
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  past_due: 'bg-red-100 text-red-700',
  paid: 'bg-blue-100 text-blue-700',
  generating: 'bg-purple-100 text-purple-700',
  review: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-green-100 text-green-700',
  delivering: 'bg-orange-100 text-orange-700',
};

const STALE_THRESHOLD_MS = 48 * 60 * 60 * 1000; // 48 hours

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hoursAgo(dateStr: string): number {
  return (Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60);
}

function formatRelativeDate(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now();
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days}d`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

function formatElapsed(dateStr: string): string {
  const hours = hoursAgo(dateStr);
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${Math.round(hours)}h`;
  return `${Math.round(hours / 24)}d`;
}

// ---------------------------------------------------------------------------
// Stage duration analytics helpers
// ---------------------------------------------------------------------------

function computeStageStats(
  orders: CompletedOrder[],
): { stages: StageDurationStats[]; total: StageDurationStats } | null {
  if (orders.length === 0) return null;

  function hoursBetween(a: string, b: string): number {
    return (new Date(b).getTime() - new Date(a).getTime()) / (1000 * 60 * 60);
  }

  function statsFor(label: string, color: string, values: number[]): StageDurationStats {
    if (values.length === 0) {
      return { label, color, values: [], min: 0, median: 0, avg: 0, max: 0 };
    }
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return {
      label,
      color,
      values: sorted,
      min: sorted[0],
      median: sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid],
      avg: sorted.reduce((a, b) => a + b, 0) / sorted.length,
      max: sorted[sorted.length - 1],
    };
  }

  const genDurations: number[] = [];
  const reviewDurations: number[] = [];
  const deliveryDurations: number[] = [];
  const totalDurations: number[] = [];

  for (const o of orders) {
    if (o.paid_at && o.generated_at) {
      genDurations.push(hoursBetween(o.paid_at, o.generated_at));
    }
    if (o.generated_at && o.approved_at) {
      reviewDurations.push(hoursBetween(o.generated_at, o.approved_at));
    }
    if (o.approved_at && o.delivered_at) {
      deliveryDurations.push(hoursBetween(o.approved_at, o.delivered_at));
    }
    if (o.paid_at && o.delivered_at) {
      totalDurations.push(hoursBetween(o.paid_at, o.delivered_at));
    }
  }

  return {
    stages: [
      statsFor('Generation', 'bg-purple-500', genDurations),
      statsFor('Review Queue', 'bg-yellow-500', reviewDurations),
      statsFor('Delivery', 'bg-orange-500', deliveryDurations),
    ],
    total: statsFor('End-to-End', 'bg-brand', totalDurations),
  };
}

function formatHours(h: number): string {
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function DeliveriesPage() {
  const [loading, setLoading] = useState(true);
  const [pipelineOrders, setPipelineOrders] = useState<PipelineOrder[]>([]);
  const [renewals, setRenewals] = useState<RenewalSubscription[]>([]);
  const [failedCount, setFailedCount] = useState(0);
  const [lastDeliveries, setLastDeliveries] = useState<Map<string, string>>(new Map());
  const [rangeFilter, setRangeFilter] = useState<RangeFilter>(7);
  const [analyticsRange, setAnalyticsRange] = useState<AnalyticsRange>(30);
  const [completedOrders, setCompletedOrders] = useState<CompletedOrder[]>([]);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  useEffect(() => {
    async function fetchData() {
      const now = new Date();
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();

      // Run the first 3 queries in parallel
      const [pipelineRes, renewalsRes, failedRes] = await Promise.all([
        // 1. Pipeline orders (active statuses)
        supabase
          .from('orders')
          .select(
            'id, status, user_id, subscription_id, updated_at, created_at, profiles(full_name, email)',
          )
          .in('status', PIPELINE_STAGES),

        // 2. Upcoming renewals (next 30 days)
        supabase
          .from('subscriptions')
          .select('id, user_id, status, pace, current_period_end, profiles(full_name, email)')
          .in('status', ['active', 'past_due'])
          .lte('current_period_end', thirtyDaysFromNow)
          .gte('current_period_end', now.toISOString())
          .order('current_period_end', { ascending: true }),

        // 3. Failed count
        supabase.from('orders').select('id', { count: 'exact', head: true }).eq('status', 'failed'),
      ]);

      const pipeline = (pipelineRes.data as unknown as PipelineOrder[]) ?? [];
      const subs = (renewalsRes.data as unknown as RenewalSubscription[]) ?? [];

      setPipelineOrders(pipeline);
      setRenewals(subs);
      setFailedCount(failedRes.count ?? 0);

      // 4. Fetch last deliveries for renewal subscriptions
      if (subs.length > 0) {
        const subIds = subs.map((s) => s.id);
        const { data: deliveredOrders } = await supabase
          .from('orders')
          .select('subscription_id, delivered_at')
          .in('subscription_id', subIds)
          .eq('status', 'delivered')
          .order('delivered_at', { ascending: false });

        if (deliveredOrders) {
          const deliveryMap = new Map<string, string>();
          for (const row of deliveredOrders) {
            // Keep only the most recent per subscription
            if (row.subscription_id && !deliveryMap.has(row.subscription_id)) {
              deliveryMap.set(row.subscription_id, row.delivered_at!);
            }
          }
          setLastDeliveries(deliveryMap);
        }
      }

      setLoading(false);
    }

    fetchData();
  }, []);

  // Fetch completed orders for stage duration analytics
  useEffect(() => {
    async function fetchAnalytics() {
      const cutoff = new Date(Date.now() - analyticsRange * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase
        .from('orders')
        .select('paid_at, generated_at, approved_at, delivered_at')
        .eq('status', 'delivered')
        .gte('delivered_at', cutoff)
        .order('delivered_at', { ascending: false });

      setCompletedOrders((data as CompletedOrder[]) ?? []);
    }

    fetchAnalytics();
  }, [analyticsRange]);

  // -------------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------------

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const stage of PIPELINE_STAGES) {
      counts[stage] = pipelineOrders.filter((o) => o.status === stage).length;
    }
    return counts;
  }, [pipelineOrders]);

  const stageAvgHours = useMemo(() => {
    const result: Record<string, number> = {};
    for (const stage of PIPELINE_STAGES) {
      const orders = pipelineOrders.filter((o) => o.status === stage);
      if (orders.length === 0) {
        result[stage] = 0;
        continue;
      }
      const total = orders.reduce((sum, o) => sum + hoursAgo(o.updated_at), 0);
      result[stage] = total / orders.length;
    }
    return result;
  }, [pipelineOrders]);

  const staleByStage = useMemo(() => {
    const result: Record<string, boolean> = {};
    for (const stage of PIPELINE_STAGES) {
      result[stage] = pipelineOrders.some(
        (o) =>
          o.status === stage && Date.now() - new Date(o.updated_at).getTime() > STALE_THRESHOLD_MS,
      );
    }
    return result;
  }, [pipelineOrders]);

  const filteredRenewals = useMemo(() => {
    const cutoff = new Date(Date.now() + rangeFilter * 24 * 60 * 60 * 1000).getTime();
    return renewals.filter(
      (s) => s.current_period_end && new Date(s.current_period_end).getTime() <= cutoff,
    );
  }, [renewals, rangeFilter]);

  const renewalsNext7d = useMemo(() => {
    const cutoff = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).getTime();
    return renewals.filter(
      (s) => s.current_period_end && new Date(s.current_period_end).getTime() <= cutoff,
    ).length;
  }, [renewals]);

  const stageAnalytics = useMemo(() => computeStageStats(completedOrders), [completedOrders]);

  const reviewCount = stageCounts['review'] ?? 0;
  const approvedCount = stageCounts['approved'] ?? 0;

  // Active orders timeline — sorted oldest first, capped at 50
  const activeTimeline = useMemo(() => {
    return [...pipelineOrders]
      .sort((a, b) => new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime())
      .slice(0, 50);
  }, [pipelineOrders]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="mb-6 font-heading text-2xl text-text-primary">Projected Deliveries</h1>

      {/* Summary stat cards */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={<CalendarClock size={24} />}
          count={renewalsNext7d}
          label="Renewals (7d)"
          href="#renewals"
        />
        <MetricCard
          icon={<ClipboardCheck size={24} />}
          count={reviewCount}
          label="Awaiting Review"
          href="/orders?status=review"
        />
        <MetricCard
          icon={<CheckCircle size={24} />}
          count={approvedCount}
          label="Ready to Deliver"
          href="/orders?status=approved"
        />
        <MetricCard
          icon={<AlertTriangle size={24} />}
          count={failedCount}
          label="Failed Deliveries"
          href="/orders?status=failed"
          warn={failedCount > 0}
        />
      </div>

      {/* Fulfillment pipeline */}
      <section className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-text-primary">Fulfillment Pipeline</h2>
        <div className="flex items-stretch gap-0 overflow-x-auto rounded-xl border border-border bg-white p-4">
          {PIPELINE_STAGES.map((stage, i) => {
            const colors = STAGE_COLORS[stage];
            const count = stageCounts[stage];
            const avgH = stageAvgHours[stage];
            const hasStale = staleByStage[stage];

            return (
              <div key={stage} className="flex items-stretch">
                <Link
                  to={`/orders?status=${stage}`}
                  className={`flex min-w-[140px] flex-col items-center justify-center rounded-lg px-5 py-4 transition-shadow hover:shadow-md ${colors.bg}`}
                >
                  <span className={`text-2xl font-bold ${colors.text}`}>{count}</span>
                  <span className={`text-xs font-medium capitalize ${colors.text}`}>{stage}</span>
                  {count > 0 && (
                    <span className="mt-1 text-[10px] text-text-muted">
                      avg {avgH < 1 ? '<1h' : `${Math.round(avgH)}h`}
                    </span>
                  )}
                  {hasStale && (
                    <span className="mt-1 flex items-center gap-0.5 text-[10px] font-medium text-red-600">
                      <AlertTriangle size={10} />
                      stale
                    </span>
                  )}
                </Link>
                {i < PIPELINE_STAGES.length - 1 && (
                  <div className="flex items-center px-2 text-gray-300">
                    <ChevronRight size={20} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Stage Duration Analytics */}
      <section className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
            <BarChart3 size={20} className="text-text-muted" />
            Stage Duration Analytics
          </h2>
          <div className="flex gap-1">
            {([30, 90] as AnalyticsRange[]).map((range) => (
              <button
                key={range}
                onClick={() => setAnalyticsRange(range)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  analyticsRange === range
                    ? 'bg-brand text-white'
                    : 'bg-gray-100 text-text-muted hover:bg-gray-200'
                }`}
              >
                {range}d
              </button>
            ))}
          </div>
        </div>

        {completedOrders.length === 0 ? (
          <p className="rounded-xl border border-border bg-white py-8 text-center text-sm text-text-muted">
            No delivered orders in the last {analyticsRange} days.
          </p>
        ) : stageAnalytics ? (
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <div className="border-b border-border bg-gray-50 px-4 py-2.5 text-xs text-text-muted">
              Based on {completedOrders.length} delivered order
              {completedOrders.length !== 1 ? 's' : ''} in the last {analyticsRange} days
            </div>
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border text-xs font-medium uppercase tracking-wider text-text-muted">
                  <th className="px-4 py-3 w-36">Stage</th>
                  <th className="px-4 py-3">Distribution</th>
                  <th className="px-4 py-3 text-right w-20">Min</th>
                  <th className="px-4 py-3 text-right w-20">Median</th>
                  <th className="px-4 py-3 text-right w-20">Avg</th>
                  <th className="px-4 py-3 text-right w-20">Max</th>
                </tr>
              </thead>
              <tbody>
                {[...stageAnalytics.stages, stageAnalytics.total].map((stage) => {
                  if (stage.values.length === 0) return null;
                  // Scale the bar relative to the total max
                  const globalMax = stageAnalytics.total.max || 1;
                  const barWidth = (stage.avg / globalMax) * 100;

                  return (
                    <tr
                      key={stage.label}
                      className={`border-b border-border-light last:border-b-0 ${
                        stage.label === 'End-to-End' ? 'bg-gray-50 font-medium' : ''
                      }`}
                    >
                      <td className="px-4 py-3 text-text-primary">{stage.label}</td>
                      <td className="px-4 py-3">
                        <div className="h-5 w-full overflow-hidden rounded bg-gray-100">
                          <div
                            className={`h-full rounded ${stage.color}`}
                            style={{ width: `${Math.max(barWidth, 2)}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-text-muted">
                        {formatHours(stage.min)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-text-primary">
                        {formatHours(stage.median)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-text-muted">
                        {formatHours(stage.avg)}
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-xs text-text-muted">
                        {formatHours(stage.max)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      {/* Upcoming renewals */}
      <section className="mb-8" id="renewals">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Upcoming Renewals</h2>
          <div className="flex gap-1">
            {([7, 14, 30] as RangeFilter[]).map((range) => (
              <button
                key={range}
                onClick={() => setRangeFilter(range)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  rangeFilter === range
                    ? 'bg-brand text-white'
                    : 'bg-gray-100 text-text-muted hover:bg-gray-200'
                }`}
              >
                {range}d
              </button>
            ))}
          </div>
        </div>

        {filteredRenewals.length === 0 ? (
          <p className="rounded-xl border border-border bg-white py-8 text-center text-sm text-text-muted">
            No renewals in the next {rangeFilter} days.
          </p>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-white">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-border bg-gray-50 text-xs font-medium uppercase tracking-wider text-text-muted">
                  <th className="px-4 py-3">Subscriber</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Renews</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last Delivery</th>
                </tr>
              </thead>
              <tbody>
                {filteredRenewals.map((sub) => {
                  const isPastDue = sub.status === 'past_due';
                  const lastDel = lastDeliveries.get(sub.id);

                  return (
                    <tr
                      key={sub.id}
                      className={`border-b border-border-light last:border-b-0 ${
                        isPastDue ? 'bg-red-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">
                          {sub.profiles?.full_name ?? 'Unknown'}
                        </p>
                        <p className="text-xs text-text-muted">{sub.profiles?.email ?? ''}</p>
                      </td>
                      <td className="px-4 py-3 capitalize text-text-muted">{sub.pace}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-text-primary">
                          {sub.current_period_end ? formatDate(sub.current_period_end) : '—'}
                        </span>
                        <span className="ml-1.5 text-xs text-text-muted">
                          {sub.current_period_end ? formatRelativeDate(sub.current_period_end) : ''}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={sub.status} />
                      </td>
                      <td className="px-4 py-3 text-text-muted">
                        {lastDel ? formatDate(lastDel) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Active orders timeline */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text-primary">Active Orders</h2>
          {pipelineOrders.length > 50 && (
            <Link to="/orders" className="text-xs font-medium text-brand hover:underline">
              View all in Orders
            </Link>
          )}
        </div>

        {activeTimeline.length === 0 ? (
          <p className="rounded-xl border border-border bg-white py-8 text-center text-sm text-text-muted">
            No active orders in the pipeline.
          </p>
        ) : (
          <div className="space-y-2">
            {activeTimeline.map((order) => {
              const _elapsed = hoursAgo(order.updated_at);
              const isStale =
                Date.now() - new Date(order.updated_at).getTime() > STALE_THRESHOLD_MS;

              return (
                <div
                  key={order.id}
                  className={`flex items-center gap-4 rounded-lg border bg-white px-4 py-3 ${
                    isStale ? 'border-red-200 bg-red-50' : 'border-border-light'
                  }`}
                >
                  <StatusBadge status={order.status} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">
                      {order.profiles?.full_name ?? 'Unknown'}
                    </p>
                    <p className="truncate text-xs text-text-muted">
                      {order.profiles?.email ?? ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-text-muted">
                    <Clock size={12} />
                    <span className={isStale ? 'font-medium text-red-600' : ''}>
                      {formatElapsed(order.updated_at)}
                    </span>
                    {isStale && <AlertTriangle size={12} className="text-red-500" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metric Card (reused pattern from DashboardPage)
// ---------------------------------------------------------------------------

interface MetricCardProps {
  icon: ReactNode;
  count: number;
  label: string;
  href: string;
  warn?: boolean;
}

function MetricCard({ icon, count, label, href, warn }: MetricCardProps) {
  return (
    <Link
      to={href}
      className="flex items-center gap-4 rounded-xl border border-border bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${
          warn ? 'bg-red-100 text-red-600' : 'bg-brand-light text-brand'
        }`}
      >
        {icon}
      </div>
      <div>
        <p className="text-3xl font-bold text-text-primary">{count}</p>
        <p className="text-sm text-text-muted">{label}</p>
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: string }) {
  const color = STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span
      className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${color}`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}
